/**
 * Utilities for normalizing and denormalizing data structures
 * to avoid duplication while maintaining component compatibility
 */

export interface NormalizedData {
    checkins: any[]
    features: Record<string, any>
    projects: Record<string, any>
    collections: Record<string, any>
    collection_items: Record<string, any>
}

/**
 * Reconstructs denormalized data from normalized structure
 * Combines collection items with their schemas and reconstructs feature/project details
 */
export function reconstructDataFromNormalized(normalizedData: any): {
    checkins: any[]
    collectionItems: Record<string, any>
    features: Record<string, any>
    projects: Record<string, any>
    collections: Record<string, any>
} {
    const checkinsRaw = normalizedData?.checkins || []
    const collectionItemsRaw = normalizedData?.collection_items || {}
    const collections = normalizedData?.collections || {}
    const features = normalizedData?.features || {}
    const projects = normalizedData?.projects || {}

    // Reconstruct collection items with their collection schemas
    const collectionItems: Record<string, any> = {}
    Object.entries(collectionItemsRaw).forEach(([itemId, item]: [string, any]) => {
        const collectionId = item.collection_id
        const collection = collections[collectionId]
        if (collection) {
            collectionItems[itemId] = {
                ...item,
                collection: collection
            }
        } else {
            collectionItems[itemId] = item
        }
    })

    // Reconstruct feature_details and project_details for each checkin
    const checkins = checkinsRaw.map((checkin: any) => ({
        ...checkin,
        feature_details: checkin.checkin_features?.map((cf: any) => features[cf.feature_id]).filter(Boolean) || [],
        project_details: checkin.checkin_projects?.map((cp: any) => projects[cp.project_id]).filter(Boolean) || []
    }))

    return {
        checkins,
        collectionItems,
        features,
        projects,
        collections
    }
}

const SIDE_MAPS = ['features', 'projects', 'collections', 'collection_items'] as const

/**
 * Folds one normalized payload into another.
 *
 * Checkins are keyed by id in first-seen order, with the incoming copy winning;
 * the side maps are unioned. Used to grow a single chat message as a batch of
 * `checkin_created` events streams in, so a live session ends up holding what
 * a reload delivers in one payload.
 */
export function mergeNormalized(base: any, incoming: any): NormalizedData {
    const byId = new Map<any, any>()
    for (const payload of [base, incoming]) {
        for (const checkin of payload?.checkins || []) {
            byId.set(checkin.id, checkin)
        }
    }

    const merged: any = { checkins: Array.from(byId.values()) }
    for (const key of SIDE_MAPS) {
        merged[key] = { ...(base?.[key] || {}), ...(incoming?.[key] || {}) }
    }
    return merged as NormalizedData
}

/**
 * Turns a denormalized checkin (as the parse endpoint returns it) back into the
 * normalized shape, so a completed parse can be merged into a message's payload
 * without the message having to hold two representations.
 */
export function normalizedFromCheckin(
    checkin: any,
    collectionItems: Record<string, any> = {}
): NormalizedData {
    const features: Record<string, any> = {}
    for (const feature of checkin?.feature_details || []) {
        features[feature.id] = feature
    }
    const projects: Record<string, any> = {}
    for (const project of checkin?.project_details || []) {
        projects[project.id] = project
    }
    return {
        checkins: checkin ? [checkin] : [],
        features,
        projects,
        collections: {},
        collection_items: collectionItems,
    }
}

